import { ClientAPI } from '@/redux/api';
import {
  type OrderLineViewModel,
  type CartResultApiModel,
  type CartViewModel,
  type LightCartViewModel,
  PromoCodeResultApiModel,
  PromoCodeRequestApiModel
} from '@/@types/client-api';

const cartServiceEndpoints = ClientAPI.injectEndpoints({
  endpoints: builder => ({
    getCart: builder.query<CartViewModel, void>({
      providesTags: [{ type: 'CartData' }],
      query: () => ({
        url: '/commerce/carts',
        method: 'GET'
      })
    }),
    postCartItem: builder.mutation<CartResultApiModel, OrderLineViewModel>({
      invalidatesTags: [{ type: 'CartData' }, { type: 'CheckoutData' }],
      query: body => ({
        url: `/commerce/carts`,
        method: 'POST',
        body
      })
    }),
    putCartItems: builder.mutation<CartResultApiModel, OrderLineViewModel[]>({
      invalidatesTags: [{ type: 'CartData' }, { type: 'CheckoutData' }],
      query: body => ({
        url: `/commerce/carts`,
        method: 'PUT',
        body
      })
    }),
    getLightCart: builder.query<LightCartViewModel, void>({
      providesTags: [{ type: 'CartData' }],
      query: () => ({
        url: '/commerce/carts/cart-items/',
        method: 'GET'
      })
    }),
    deleteCartItem: builder.mutation<void, OrderLineViewModel[]>({
      invalidatesTags: [{ type: 'CartData' }, { type: 'CheckoutData' }],
      query: body => ({
        url: `/commerce/carts/delete`,
        method: 'POST',
        body
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        const deletedCodes = new Set(body.map(item => item.code));
        const patch = dispatch(
          cartServiceEndpoints.util.updateQueryData('getCart', undefined, cart => {
            cart.orderLines = cart.orderLines?.filter(item => !deletedCodes.has(item.code));
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    }),
    updateCartItem: builder.mutation<void, OrderLineViewModel[]>({
      invalidatesTags: [{ type: 'CartData' }, { type: 'CheckoutData' }],
      query: body => ({
        url: `/commerce/carts`,
        method: 'PUT',
        body
      })
    }),
    applyPromoCode: builder.mutation<PromoCodeResultApiModel, PromoCodeRequestApiModel>({
      invalidatesTags: [{ type: 'CartData' }, { type: 'CheckoutData' }],
      query: body => ({
        url: `/commerce/carts/promo-code`,
        method: 'PUT',
        body
      })
    })
  })
});

export const {
  useGetCartQuery,
  usePostCartItemMutation,
  usePutCartItemsMutation,
  useGetLightCartQuery,
  useDeleteCartItemMutation,
  useUpdateCartItemMutation,
  useApplyPromoCodeMutation
} = cartServiceEndpoints;

export default cartServiceEndpoints;
